// REACT IMPORTS
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import QuickCheckoutLoginLink from "components/auth/QuickCheckoutLoginLink";

// COMPONENT IMPORTS
import InputField from "components/form/InputField";
import Button from "components/form/Button";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useAccountDetails, userLogin } from "store/slices/authSlice";
import { companyMarkersGet } from "store/slices/paymentsSlice";
import {
  portalSettingsGet,
  usePortalSettingsData,
} from "store/slices/dashboardSlice";
import { RootState } from "store/store";
import { InfoRedCircleIcon } from "icons/subscription/InfoRedCircleIcon";
import {
  decode,
  formatContent,
  replaceMarkers,
  validateEmail,
} from "utils/HelperFunctions";
import { isGoogleLoginEnabled } from "utils/googleLogin";
import GoogleLoginButton from "./GoogleLoginButton";
import SupportPhoneLink from "./SupportPhoneLink";
import { useGoogleAuth } from "hooks/useGoogleAuth";
import { useTid } from "utils/testid";

/* ------------------------------------- LOGIN VIA CRED FORM ------------------------------------- */

const LoginViaCredForm = ({
  guestLogin,
  setShowOverlay,
  onOtpRequired,
}: any) => {
  const tid = useTid();
  const initialVal = {
    email: "",
    password: "",
    isPasswordLogin: true,
  };

  const dispatch = useAppDispatch();
  const accountDetails = useAccountDetails();
  const { companyMarkers } = useAppSelector(
    (state: RootState) => state.payment
  );
  const portalSettings = usePortalSettingsData();
  const { portalSettingsStatus, portalSettingsClusterId } = useAppSelector(
    (state: RootState) => state.dashboard
  );

  const [isLoading, setLoading] = useState<boolean>(false);
  const [values, setValues] = useState<any>(initialVal);
  const [isErr, setIsErr] = useState<boolean>(false);
  const [isFormatErr, setIsFormatErr] = useState<boolean>(false);
  // The email and password open more than one account; the Account ID picks.
  const [needsAccountId, setNeedsAccountId] = useState<boolean>(false);
  const [loginText, setLoginText] = useState<string>("");
  const { clusterId: cluster_id } = useParams();
  const clusterId = decode(cluster_id || "", true);
  useEffect(() => {
    if (isErr) {
      setIsErr(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  useEffect(() => {
    // Markers hold the branding Support Phone; fetch them on direct page loads
    if (!companyMarkers?.["Support Phone"] && clusterId) {
      dispatch(companyMarkersGet({ cluster_id: clusterId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterId]);

  useEffect(() => {
    // This page has no loader of its own for the welcome message; the login
    // page has already fetched it when the customer comes from there
    if (clusterId && portalSettingsClusterId !== clusterId) {
      dispatch(portalSettingsGet({ cluster_id: clusterId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterId]);

  const hasWelcomeMessage =
    ["C", "D"].includes(portalSettings?.pp_welcome_msg_flag) &&
    Boolean(portalSettings?.pp_welcome_msg_value);
  const settingsSettled =
    portalSettingsStatus === "succeeded" || portalSettingsStatus === "failed";

  useEffect(() => {
    // Wait for the markers, which can arrive after the settings on a direct
    // page load, so the customer never sees the raw placeholders
    if (hasWelcomeMessage && Object.keys(companyMarkers || {}).length > 0) {
      const sanitizedHtml = replaceMarkers(
        portalSettings.pp_welcome_msg_value,
        companyMarkers
      );

      setLoginText(formatContent(sanitizedHtml));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalSettings, companyMarkers]);

  const checkIsDisabled = () => {
    return Object.values(values).some(
      (value) => typeof value === "string" && value.trim() === ""
    );
  };

  const { onGoogleCredential, completeLogin } = useGoogleAuth({
    guestLogin,
    setShowOverlay,
    onOtpRequired,
    onStart: () => setLoading(true),
    onFinish: () => setLoading(false),
    onNoMatch: () => setIsErr(true),
  });

  const onSubmit = async (e: any) => {
    e.preventDefault();
    if (!validateEmail(values?.email || "")) {
      setIsFormatErr(true);
      return;
    }
    setIsFormatErr(false);
    setLoading(true);
    try {
      let payload: any = { ...values };
      payload["clusterId"] = clusterId;
      const res: any = await dispatch(userLogin(payload)).unwrap();

      if (res && res?.body?.success) {
        // Same post-login steps as the field login, including the OTP screen
        // when the cluster has two-factor on.
        completeLogin(res);
      } else if (res?.body?.data?.needsAccountId && !needsAccountId) {
        setNeedsAccountId(true);
        setValues((current: any) => ({ ...current, accountId: "" }));
      } else {
        setIsErr(true);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      console.log("Login Err: --->", error?.message);
    }
  };

  return (
    <div
      className="animate__animated animate__fadeIn"
      data-testid={tid("form-shell")}
    >
      {hasWelcomeMessage ? (
        loginText && (
          <div
            id="welcome-content"
            className="welcome-message max-w-[520px] pb-10"
            style={{ wordBreak: "keep-all", overflowWrap: "normal" }}
            dangerouslySetInnerHTML={{ __html: loginText }}
          ></div>
        )
      ) : settingsSettled ? (
        <>
          <div className="lg:text-3xl text-xl font-bold -tracking-[0.6px] leading-normal text-txtPrimary">
            Welcome - Secure Payment Portal
          </div>
          <p className="lg:py-10 pt-6 pb-10 max-w-[520px] text-txtParagraph lg:text-base text-sm leading-normal font-normal">
            Please have your account information ready for identification
            purposes. If you are having trouble logging in, please contact our
            representative at{" "}
            <SupportPhoneLink
              phone={companyMarkers?.["Support Phone"]}
              className="underline"
            />
            .
          </p>
        </>
      ) : null}
      <form onSubmit={onSubmit} noValidate data-testid={tid("password-login-form")}>
        <div className="max-w-[520px] flex flex-col lg:gap-[60px] gap-10">
          <div className="flex flex-col lg:gap-6 gap-5">
            <div className="flex flex-col gap-3">
              <InputField
                type="email"
                name="email"
                label="Email"
                data-testid={tid("email-input")}
                aria-label="Email address"
                placeholder="Enter your email address"
                value={values?.email}
                onChange={(e: any) => {
                  if (isFormatErr) {
                    setIsFormatErr(false);
                  }
                  // A different email may belong to a single account.
                  const { accountId, ...rest } = values;
                  setNeedsAccountId(false);
                  setValues({
                    ...rest,
                    email: e?.target?.value,
                  });
                }}
                _className="!font-medium"
              />
              {isFormatErr && (
                <div
                  className="flex items-center gap-[5px] px-[10px] py-3 bg-lightRedBg rounded select-none animate__animated animate__fadeIn"
                  role="alert"
                  data-testid={tid("email-format-alert")}
                >
                  <InfoRedCircleIcon aria-hidden="true" />
                  <div className="lg:text-base text-sm text-red leading-[20.83px]">
                    Please enter a valid email address.
                  </div>
                </div>
              )}
            </div>
            <div>
              <InputField
                type="password"
                name="password"
                label="Password"
                data-testid={tid("password-input")}
                aria-label="Password"
                placeholder="Enter password"
                value={values?.password}
                onChange={(e: any) => {
                  setValues({
                    ...values,
                    password: e?.target?.value,
                  });
                }}
                _className="!font-medium"
              />
              <div className="flex justify-end">
                <Link
                  to={`/${cluster_id}/forgot-password`}
                  className="pt-1 cursor-pointer text-end text-blue hover:!text-btnPrimaryHover lg:text-base text-sm font-medium leading-normal underline"
                  data-testid={tid("forgot-password-link")}
                  aria-label="Forgot password"
                >
                  Forgot password?
                </Link>
              </div>
            </div>
            {needsAccountId && (
              <div className="flex flex-col gap-3">
                <InputField
                  type="text"
                  name="accountId"
                  label="Account ID"
                  data-testid={tid("account-id-input")}
                  aria-label="Account ID"
                  aria-describedby="password-login-account-id-hint"
                  placeholder="Enter Account ID"
                  value={values?.accountId ?? ""}
                  onChange={(e: any) => {
                    setValues({
                      ...values,
                      accountId: e?.target?.value,
                    });
                  }}
                  _className="!font-medium"
                />
                <p
                  id="password-login-account-id-hint"
                  className="lg:text-base text-sm text-txtParagraph leading-normal"
                  data-testid={tid("account-id-hint")}
                >
                  This email is linked to more than one account. Enter your
                  Account ID.
                </p>
              </div>
            )}
          </div>
          <div>
            <Button
              isLoading={isLoading}
              className="w-full"
              text="Login"
              type="submit"
              data-testid={tid("login-submit-button")}
              aria-label="Log in with email and password"
              isDisabled={checkIsDisabled()}
              variant="variable"
            />
            {isErr && (
              <div
                className="flex items-center gap-[5px] mt-3 px-[10px] py-3 bg-lightRedBg rounded select-none animate__animated animate__fadeIn"
                role="alert"
                data-testid={tid("login-error-alert")}
              >
                <InfoRedCircleIcon aria-hidden="true" />
                <div className="lg:text-base text-xs text-red leading-[20.83px]">
                  Login failed. Please check your credentials and try again.
                </div>
              </div>
            )}
            {isGoogleLoginEnabled(accountDetails?.data) ? (
              <div className="flex flex-col gap-3 mt-4">
                <div className="flex items-center gap-[6px]">
                  <div className="h-[1px] w-full bg-strokePrimary" />
                  <div className="flex whitespace-nowrap text-sm text-txtSecondary font-normal leading-normal">
                    or
                  </div>
                  <div className="h-[1px] w-full bg-strokePrimary" />
                </div>
                <GoogleLoginButton
                  onCredential={onGoogleCredential}
                  data-testid={tid("google-login-button")}
                />
              </div>
            ) : null}
          </div>
        </div>
      </form>
      {!isErr && (
        <div className="max-w-[520px] flex flex-col lg:gap-4 gap-3 items-center pt-3 lg:pt-4">
          {accountDetails &&
            accountDetails?.data &&
            (accountDetails?.data.login_type === "BOTH" ||
              accountDetails?.data.login_type === "CD") && (
              <Link
                to={`/${cluster_id}/login`}
                className="lg:text-base text-sm font-medium text-blue hover:!text-btnPrimaryHover leading-normal cursor-pointer"
                data-testid={tid("use-data-login-link")}
                aria-label="Validate your data instead"
              >
                Validate your data instead
              </Link>
            )}
          <div className="h-[1px] w-full bg-strokePrimary" />
          <div className="flex gap-[6px] items-center justify-start w-full">
            <p className="lg:text-base text-sm text-txtParagraph leading-normal">
              Don’t have an account?
            </p>
            <Link
              to={`/${cluster_id}/sign-up`}
              className="cursor-pointer lg:text-base text-sm text-blue hover:!text-btnPrimaryHover font-medium leading-normal"
              data-testid={tid("sign-up-link")}
              aria-label="Sign up"
            >
              Sign up
            </Link>
          </div>
          <QuickCheckoutLoginLink
            guestLogin={guestLogin}
            testId={tid("quick-checkout-link")}
          />
        </div>
      )}
    </div>
  );
};

export default LoginViaCredForm;
